import {useMemo, useState} from "react";
import {useParams} from "react-router";
import {Link} from "react-router-dom";
import {css, Theme} from "@emotion/react";
import {
    borderRadius,
    calculateRemSize,
    flex,
    flexDirection,
    mb,
    mt,
    onDesktop,
    p,
    pb,
    pl,
    pt
} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";
import {compile} from "path-to-regexp";

import {chartsRoutes} from "../../../../common/app/routing/charts_routes";
import {showOnHoverScroll} from "../../../../components/css_helpers/scroll";
import {IEntryCategory} from "../../types/Category";
import {CategoriesMenuMobile} from "./CategoriesMenuMobile";

interface IEntryCategoriesAsideProps {
    className?: string;
    categories: IEntryCategory[];
}

export const EntryCategories = ({className, categories}: IEntryCategoriesAsideProps) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const {marketInfoCategorySlug, marketInfoSubCategorySlug} = useParams<{
        marketInfoCategorySlug?: string;
        marketInfoSubCategorySlug?: string;
    }>();

    const toPath = useMemo(() => {
        return compile(chartsRoutes.pl.newshub.marketInformation.category.subcategory.root);
    }, []);

    const toPathCategory = useMemo(() => {
        return compile(chartsRoutes.pl.newshub.marketInformation.category.root);
    }, []);

    return (
        <aside css={stickyAside} className={className}>
            <div css={wrapper}>
                <div css={mobile}>
                    <CategoriesMenuMobile
                        categories={categories}
                        isOpen={isMobileMenuOpen}
                        setOpen={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    />
                </div>

                <div css={[desktop, categoriesWrapper]}>
                    <Text variant="mini_header" as="div" css={[pl(2), mb(2)]}>
                        Wybierz kategorię
                    </Text>

                    {categories.map((category) => {
                        const isActiveCategory = category.slug === marketInfoCategorySlug;
                        const hasSubcategories = category.subcategories.length > 0;

                        return (
                            <div key={category.slug} css={categoryGroup}>
                                <div css={categoryHeader}>
                                    <Link
                                        to={toPathCategory({
                                            marketInfoCategorySlug: category.slug
                                        })}
                                    >
                                        <Text variant="headline_6" css={isActiveCategory && activeLink}>
                                            {category.name}
                                        </Text>
                                    </Link>
                                </div>

                                {hasSubcategories &&
                                    category.subcategories.map((subcategory) => {
                                        const isActiveSubcategory =
                                            isActiveCategory && subcategory.slug === marketInfoSubCategorySlug;

                                        return (
                                            <div key={subcategory.name} css={subcategoryItem}>
                                                <Link
                                                    to={toPath({
                                                        marketInfoCategorySlug: category.slug,
                                                        marketInfoSubCategorySlug: subcategory.slug
                                                    })}
                                                >
                                                    <Text
                                                        variant="body_copy_2"
                                                        css={[divider, isActiveSubcategory && activeLink]}
                                                    >
                                                        {subcategory.name}
                                                    </Text>
                                                </Link>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </aside>
    );
};

const mobile = css`
    display: block;

    ${onDesktop(css`
        display: none;
    `)};
`;

const desktop = css`
    display: none;

    ${onDesktop(css`
        display: block;
    `)};
`;

const stickyAside = css`
    ${mt(2)};
    ${flex()};
    ${flexDirection("column")};

    ${onDesktop(css`
        max-height: calc(100vh - 6rem);
        position: sticky;
        top: ${calculateRemSize(2)};
    `)};
`;

const wrapper = css`
    overflow-y: auto;
    ${showOnHoverScroll()};
`;

const asideElement = css`
    ${borderRadius(2)}

    ${onDesktop(css`
        background-color: #fff;
    `)};
`;

const categoriesWrapper = css`
    ${mb(3)};
    ${pt(2.5)};
    ${pb(1)};
    ${asideElement}
`;

const categoryGroup = css`
    ${mb(1.5)};
`;

const categoryHeader = css`
    ${p(1.5, 2)};
`;

const subcategoryItem = css`
    ${p(1.5, 2, 1.5, 3)};
`;

const divider = (theme: Theme) => css`
    border-bottom: 1px solid ${theme.colors.gray[200]};
    ${pb(2)};
`;

const activeLink = (theme: Theme) => css`
    color: ${theme.colors.secondary};
    font-weight: 700;
    text-decoration: underline;
    text-decoration-color: ${theme.colors.primary};
    text-decoration-thickness: 2px;
`;
